import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";

import {
  TABLE_CONTACTS_LIST,
  CONTACT_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { loadAllContact } from "./ContactList.actionCreators";
import { setAllContactLoadingState } from "./ContactList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";
import ContactId from "./components/ContactId/ContactId";
import Communication from "./components/Communication/Communication";
import ContactAction from "./components/ContactAction/ContactAction";
import ContactJob from "./components/ContactJob/ContactJob";
import CompanyLink from "./components/CompanyLink/CompanyLink";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    companyId,
    createContact,
    contactType,
    dependancies,
    page,
    companyCountryId,
  } = props;

  const [data, setData] = useState([]);
  const [status, setStatus] = useState(2);
  const [platformUser, setPlatformUser] = useState(0);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);

  const mainCountryInfo = UserInfo.countryInfo();

  const countryId = UserInfo.countryId();
  const countryList = UserInfo.countryListForFilter(t);

  const [selectedFilter, setSelectedFilter] = useState([
    {
      column: "country",
      value: {
        value: page === "company-view" ? companyCountryId : mainCountryInfo?.country?.id,
        label: t(mainCountryInfo?.country?.name),
      },
    },
  ]);

  const dataURL = CONTACT_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "idInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <ContactId
          data={"#" + value.id}
          id={value.id}
          status={value.status}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_COMPANY"),
      title: "CONTACT_COMPANY",
      accessor: "company",
      Cell: ({ cell: { value } }) => <CompanyLink data={value} />,
    },
    {
      Header: t("CONTACT_NAME"),
      title: "CONTACT_NAME",
      accessor: "contactName",
      Cell: ({ cell: { value } }) => (
        <ContactId
          data={value.name}
          id={value.id}
          status={value.status}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_FUNCTION"),
      title: "CONTACT_FUNCTION",
      accessor: "jobInfo",
      Cell: ({ cell: { value } }) => (
        <ContactJob data={value.name} status={value.status} />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_TYPE"),
      title: "CONTACT_TYPE",
      accessor: "rolesInfo",
      Cell: ({ cell: { value } }) => (
        <ContactJob data={value.name} status={value.status} />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_COMMUNICATION"),
      title: "CONTACT_COMMUNICATION",
      accessor: "communication",
      Cell: ({ cell: { value } }) => <Communication data={value} />,
      disableFilters: true,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <ContactAction
          data={value}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const contactListByIds = useSelector((state) => state.contactList.byId);
  const contactListAllIds = useSelector((state) => state.contactList.allIds);
  const filteredRecords = useSelector(
    (state) => state.contactList.filteredRecords
  );

  const isContactListLoading = useSelector(
    (state) => state.contactList.isLoading
  );

  useEffect(() => {
    setLoading(isContactListLoading);
  }, [isContactListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
      companyId,
      dependancies,
    });
    // Set the loading state
    let selectedCountries = [];
    if (additionalFilters.length > 0) {
      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      selectedCountries = countryTypeFilter?.map((item) => item.value);
    }
    if (selectedCountries.length === 0) {
      selectedCountries = page === "company-view" ? companyCountryId : countryId;
    }
    setLoading(true);
    dispatch(setAllContactLoadingState());
    dispatch(
      loadAllContact({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status,
        platformUser,
        companyId,
        dependancies,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    if (contactListAllIds.length > 0) {
      const responseData = contactListAllIds.map((id) => {
        const tempData = { ...contactListByIds[id] };
        tempData.idInfo = {
          id: tempData.id,
          status: tempData.status,
        };
        tempData.contactName = {
          id: tempData.id,
          name: tempData.firstName + " " + tempData.lastName,
          status: tempData.status,
        };
        tempData.company = {
          id: tempData?.company?.id ?? "",
          name: tempData?.company?.name ?? t("CONTACT_COMPANY_NONE"),
          status: tempData.status,
        };
        let roles = [];
        if (tempData.roles !== null && tempData.roles !== undefined) {
          roles.push(
            tempData.roles
              .reduce((p, c) => [...p, t(c.roleLabel)], [])
              .join(", ")
          );
        }
        tempData.rolesInfo = {
          name: roles,
          status: tempData.status,
        };
        tempData.jobInfo = {
          name: tempData.job,
          status: tempData.status,
        };

        tempData.communication = {
          email: tempData.email,
          mobilePhone: tempData.mobilePhone,
          phone: tempData.phone,
          status: tempData.status,
        };
        tempData.action = {
          id: tempData.id,
          status: tempData.status,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, contactListAllIds, contactListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    if (label === "platform") {
      let platformNew = platformUser === 1 ? 0 : 1;
      setPlatformUser(platformNew);
    } else {
      let statusNew = status === 1 ? 2 : 1;
      setStatus(statusNew);
    }
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        companyId: requestData.companyId,
        dependancies,
        appliedFilters: additionalFilters,
      });
    }
  }, [status, platformUser, additionalFilters]);

  return (
    <EncloseTable
      type={contactType}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={30}
      createNewAction={createContact}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      selectedFilter={selectedFilter}
      extraFilters={[
        ...(countryList?.length > 1 && page !== "company-view"
          ? [
              {
                type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                label: t("COUNTRY"),
                name: "country",
                labelClass: "company-list__admins-list-label",
                containerClass: "company-list__admins-list-dropdown-container",
                options: countryList,
                placeholder: t("SEARCH"),
                defaultOption: "Nothing Selected",
              },
            ]
          : []),
        {
          type: FILTER_TYPE_BUTTON,
          label: "CONTACT_LIST_TOGGLE_ACTIVE_USERS",
          name: "users",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
        {
          type: FILTER_TYPE_BUTTON,
          label: "CONTACT_LIST_TOGGLE_PLATFORM_USERS",
          name: "platform",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status: platformUser,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  companyId: PropTypes.number,
  contactType: PropTypes.string,
  dependancies: PropTypes.string,
  page: PropTypes.string.isRequired,
};
PrepareDataTable.defaultProps = {
  companyId: null,
  dependancies: "",
  contactType: TABLE_CONTACTS_LIST,
};

export default withTranslation()(PrepareDataTable);
